import React, { useContext, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Box, Button, Icon, Text, theme } from '@factorialco/deprecated-design-system'
import { EnvironmentContext } from 'config/environment'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  BillingAddonReferenceFragment,
  ModulesBillingAddonReference,
} from 'generated/resources/graphql'
import { CanSelfUpsellDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import SelfUpsellSubscription from 'modules/marketplace_modules/components/SelfUpsellSubscription'
import ShareLinkModal from 'modules/marketplace_modules/components/ShareLinkModal'
import useCheckModuleOnTrial from 'modules/marketplace_modules/hooks/useCheckModuleOnTrial'
import useCreateSubscriptionOverload from 'modules/marketplace_modules/hooks/useCreateSubscriptionOverload'
import useSubscriptionOverloadEvent from 'modules/marketplace_modules/hooks/useSubscriptionOverloadEvent'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

type CTAProps = {
  handleSubscriptionOverload: () => void
  isAcquired: boolean
  upsellOrigin: string
  chargebeeModuleName?: AvailableModules
  chargebeeId?: string
  addonReference?: BillingAddonReferenceFragment[]
  crossSellingAddonReference?: ModulesBillingAddonReference[]
}

const ModuleCTA = ({
  handleSubscriptionOverload,
  isAcquired,
  chargebeeModuleName,
  chargebeeId,
  upsellOrigin,
  addonReference,
  crossSellingAddonReference,
}: CTAProps) => {
  const { company } = useCurrentSession()
  const { ignoreUpsell } = useContext(EnvironmentContext)
  const inFreeTrial = company.subscription.inFreeTrial
  const buttonType = isAcquired ? 'secondary' : 'upgrade'
  const { data: policies } = useQuery(CanSelfUpsellDocument)
  const invalidSelfUpsellModules = ['sales']
  const currentSeats = company.subscription.currentCycleQuantity || company.subscription.seatCount
  const isLargeSegment = currentSeats >= 50

  if (!chargebeeModuleName) return null

  const isValidModule = !invalidSelfUpsellModules.includes(chargebeeModuleName)
  const canSelfUpsell =
    policies?.billing._policies.subscriptionUpgradeSubscription.can && isValidModule
  const showSelfUpsellButton = canSelfUpsell || ignoreUpsell

  if (chargebeeModuleName === 'ats' && inFreeTrial) {
    return (
      <Text.Normal color="grey600">{i18n.t('marketplace.not_enabled.description')}</Text.Normal>
    )
  }

  if (inFreeTrial) {
    if (!chargebeeId) return null
    return (
      <Button type={buttonType} onClick={handleSubscriptionOverload}>
        {i18n.t('marketplace.modules.uninstall_module')}
      </Button>
    )
  }

  if (showSelfUpsellButton && addonReference?.length && !isAcquired) {
    return (
      <>
        {isLargeSegment && (
          <UpsellingKit.UpsellButton
            moduleRequested={chargebeeModuleName}
            requestSource={upsellOrigin}
            requestReason={ValidReasons.MissingAddon}
            pageSource={upsellOrigin}
            variant="outlinePromote"
          />
        )}
        <SelfUpsellSubscription
          addonReference={addonReference}
          crossSellingAddonReference={crossSellingAddonReference}
          moduleName={chargebeeModuleName}
        />
      </>
    )
  }

  return (
    <>
      {!isAcquired && (
        <UpsellingKit.UpsellButton
          moduleRequested={chargebeeModuleName}
          requestSource={upsellOrigin}
          requestReason={ValidReasons.MissingAddon}
          pageSource={upsellOrigin}
        />
      )}
    </>
  )
}

type ContactProps = {
  data: {
    icon?: React.ComponentProps<typeof Icon.Large>['icon']
    image?: { url: string; alt: string; logoBackground?: string }
    title: string
    description: string
    chargebeeModuleId?: string
    chargebeeModuleName?: AvailableModules
    billingAddonReference?: BillingAddonReferenceFragment[]
    crossSellingBillingAddonReference?: ModulesBillingAddonReference[]
  }
  moduleId: string
  isAcquired?: boolean
}

const Contact = ({ data, moduleId, isAcquired }: ContactProps) => {
  const { company: currentCompany } = useCurrentSession()
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const { icon, image, title, description } = data
  const { isModuleOnTrial } = useCheckModuleOnTrial()
  const isOnTrial = isModuleOnTrial(data.chargebeeModuleId)
  const { navigation } = useNavigation()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isPopularModule = queryParams.get('pm') === 'true'
  const upsellOrigin = queryParams.get('upsell_origin')

  const createSubscriptionOverload = useCreateSubscriptionOverload([currentCompany.id])
  const { eventEmitter } = useSubscriptionOverloadEvent({
    moduleName: moduleId,
    chargebeeId: data.chargebeeModuleId || '',
    isPopularModule,
  })

  const handleSubscriptionOverload = async () => {
    if (!data.chargebeeModuleId) return
    if (data.chargebeeModuleName === 'ats') return

    const overloadReason = isOnTrial ? 'disable_module' : 'enable_module'

    await createSubscriptionOverload({
      chargebeeId: data.chargebeeModuleId,
      overloadReason,
    })

    eventEmitter(
      isOnTrial
        ? trackingEvents.marketplaceModules.disableModule
        : trackingEvents.marketplaceModules.enableModule
    )
    window.location.href = navigation.discover.marketplaceModules.fullPath()
  }

  const shareLink = () => {
    setIsShareModalOpen((prev) => !prev)
  }

  const getLogoBackground = () => {
    if (image !== undefined) {
      if (image.logoBackground !== undefined && image.logoBackground !== '') {
        return image.logoBackground
      } else {
        return theme.colors.grey000White
      }
    } else {
      return theme.colors.grey200
    }
  }

  return (
    <>
      <Box
        width="full"
        padding="s24"
        border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
        borderRadius={{ all: 'abs012' }}
        background="grey100"
        flexDirection="row"
        gap="s24"
        justifyContent="spaceBetween"
        flexWrap="wrap"
        maxWidth="widePage"
        style={{ alignSelf: 'center' }}
      >
        <Box flexDirection="row" gap="s24" alignItems="center">
          <Box
            alignItems="center"
            justifyContent="center"
            padding="s12"
            width="s64"
            height="s64"
            borderRadius={{ all: 'abs012' }}
            style={{ backgroundColor: getLogoBackground() }}
          >
            {icon !== undefined && <Icon.Large icon={icon} color="grey800" />}
            {icon === undefined && image !== undefined ? (
              <img src={image.url} alt={image.alt} />
            ) : null}
          </Box>
          <Box maxWidth="s600">
            <Text size="500" weight="semibold" color="grey1000">
              {title}
            </Text>
            <Text.Normal>{description}</Text.Normal>
          </Box>
        </Box>
        <Box
          flexDirection="row"
          gap="s8"
          flexWrap="wrap"
          justifyContent="flexEnd"
          alignItems="center"
          alignSelf="center"
        >
          {isAcquired && <Button type="secondary" onClick={shareLink} iconName="link" />}
          <ModuleCTA
            handleSubscriptionOverload={handleSubscriptionOverload}
            isAcquired={isAcquired ?? false}
            chargebeeId={data.chargebeeModuleId}
            chargebeeModuleName={data.chargebeeModuleName}
            upsellOrigin={`From marketplace ${data.chargebeeModuleName}${
              upsellOrigin ? ` - ${upsellOrigin}` : ''
            }`}
            addonReference={data.billingAddonReference}
            crossSellingAddonReference={data.crossSellingBillingAddonReference}
          />
        </Box>
      </Box>
      {isShareModalOpen && (
        <ShareLinkModal
          moduleTitle={data.title}
          moduleUrl={window?.location?.href}
          onClose={() => setIsShareModalOpen(false)}
        />
      )}
    </>
  )
}

export default Contact
